import React, { useState, useRef, useEffect } from 'react';

export interface SelectOption {
    value: string | number;
    label: string;
    disabled?: boolean;
}

export interface SelectProps {
    options: SelectOption[];
    value?: string | number;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    onChange?: (value: string | number) => void;
    error?: string;
    label?: string;
    icon?: React.ReactNode;
    size?: 'sm' | 'md' | 'lg';
}

const Select: React.FC<SelectProps> = ({
    options,
    value,
    placeholder = 'Selecciona una opción',
    disabled = false,
    className = '',
    onChange,
    error,
    label,
    icon,
    size = 'md',
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState<SelectOption | null>(
        options.find(opt => opt.value === value) || null
    );
    const selectRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const option = options.find(opt => opt.value === value);
        setSelectedOption(option || null);
    }, [value, options]);

    const handleOptionClick = (option: SelectOption) => {
        if (option.disabled) return;

        setSelectedOption(option);
        setIsOpen(false);
        onChange?.(option.value);
    };

    // Clases para diferentes tamaños
    const getSizeClasses = () => {
        switch (size) {
            case 'sm':
                return {
                    container: 'min-h-[2rem]',
                    padding: 'px-2 py-1',
                    text: 'text-sm',
                    icon: 'w-4 h-4',
                    option: 'px-2 py-1 text-sm',
                    label: 'text-xs'
                };
            case 'lg':
                return {
                    container: 'min-h-[3rem]',
                    padding: 'px-4 py-3',
                    text: 'text-base',
                    icon: 'w-6 h-6',
                    option: 'px-4 py-3 text-base',
                    label: 'text-sm'
                };
            default: // md
                return {
                    container: 'min-h-[2.5rem]',
                    padding: 'px-3 py-2',
                    text: 'text-sm',
                    icon: 'w-5 h-5',
                    option: 'px-3 py-2 text-sm',
                    label: 'text-sm'
                };
        }
    };

    const sizeClasses = getSizeClasses();

    const baseClasses = `
    relative w-full border rounded-lg bg-white transition-colors duration-200
    ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
    ${error ? 'border-red-500' : 'border-gray-300 hover:border-gray-400'}
    ${isOpen ? 'border-blue-500 ring-2 ring-blue-500/20' : ''}
  `;

    return (
        <div className={`${className}`}>
            {label && (
                <label className={`block font-medium text-gray-700 mb-1 ${sizeClasses.label}`}>
                    {label}
                </label>
            )}

            <div ref={selectRef} className={baseClasses}>
                <div
                    className={`flex items-center justify-between ${sizeClasses.padding} ${sizeClasses.container}`}
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                >
                    <div className="flex items-center gap-2 flex-1">
                        {icon && (
                            <span className={`text-gray-400 flex-shrink-0`}>
                                {icon}
                            </span>
                        )}
                        <span className={`${selectedOption ? 'text-gray-900' : 'text-gray-400'} ${sizeClasses.text}`}>
                            {selectedOption ? selectedOption.label : placeholder}
                        </span>
                    </div>
                    <svg
                        className={`${sizeClasses.icon} text-gray-400 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''
                            }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                        />
                    </svg>
                </div>

                {isOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                        {options.map((option) => (
                            <div
                                key={option.value}
                                className={`
                  ${sizeClasses.option} cursor-pointer transition-colors duration-150
                  ${option.disabled
                                        ? 'text-gray-400 cursor-not-allowed'
                                        : 'hover:bg-gray-50 text-gray-900'
                                    }
                  ${selectedOption?.value === option.value ? 'bg-blue-50 text-blue-700' : ''}
                `}
                                onClick={() => handleOptionClick(option)}
                            >
                                {option.label}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {error && (
                <p className={`mt-1 text-red-600 ${sizeClasses.label}`}>{error}</p>
            )}
        </div>
    );
};

export default Select;